import {
  AppConfigResponseSchema,
  AuthSessionResponseSchema,
  BOT_CHECK_HEADER,
  CLIENT_PLATFORM_HEADER,
  CLIENT_VERSION_HEADER,
  EmailCodeSentSchema,
  EmailVerifyResponseSchema,
  MeResponseSchema,
  SessionResponseSchema,
  SyncResponseSchema,
  type AuthTokens,
  type Platform,
  type RefreshRequest,
  type StationChoiceRequest,
  type SyncRequest,
} from "@station-keeping/contracts";
import type { z } from "zod";
import { ApiError, NetworkError } from "./errors";

/**
 * Comment le client s'authentifie. Sur le web, les jetons vivent dans des cookies httpOnly posés par l'API :
 * le jeu ne les voit jamais. Les apps (lot Capacitor) passeront par un jeton Bearer et garderont le refresh
 * token dans le stockage sécurisé de l'appareil.
 */
export interface AuthTransport {
  readonly platform: Platform;
  /** En-têtes d'authentification de chaque requête. */
  headers(): Record<string, string>;
  /** Corps de `POST /auth/refresh` et `/auth/logout`. */
  refreshBody(): RefreshRequest;
  /** Jetons reçus (apps uniquement) ; `null` : session terminée. */
  save(tokens: AuthTokens | null): void;
}

export const cookieTransport: AuthTransport = {
  platform: "web",
  headers: () => ({}),
  refreshBody: () => ({}),
  save: () => undefined,
};

export interface Timed<T> {
  data: T;
  /** Instants d'envoi et de réception (`Date.now()`), pour caler l'horloge sur celle du serveur. */
  sentAt: number;
  receivedAt: number;
}

interface RequestOptions<S extends z.ZodType | undefined> {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  schema?: S;
  /** Jeton Turnstile (demande de code). */
  botCheck?: string;
  /** Requête authentifiée : un 401 tente un rafraîchissement, puis la rejoue une fois. */
  auth?: boolean;
  /** Survit à la fermeture de la page (envoi à la mise en arrière-plan). */
  keepalive?: boolean;
  timeoutMs?: number;
}

type Result<S> = S extends z.ZodType ? z.infer<S> : undefined;

const DEFAULT_TIMEOUT_MS = 15_000;

/** Client HTTP de l'API : en-têtes de plateforme et de version, cookies, rafraîchissement de session. */
export class ApiClient {
  private refreshing: Promise<boolean> | null = null;

  constructor(
    private readonly baseUrl: string,
    private readonly version: string,
    private readonly transport: AuthTransport = cookieTransport,
    private readonly fetchImpl: typeof fetch = (...args) => globalThis.fetch(...args),
  ) {}

  get platform(): Platform {
    return this.transport.platform;
  }

  async timed<S extends z.ZodType | undefined = undefined>(path: string, options: RequestOptions<S> = {}): Promise<Timed<Result<S>>> {
    const auth = options.auth ?? true;
    let sentAt = Date.now();
    let response = await this.send(path, options, auth);
    if (response.status === 401 && auth && (await this.refresh())) {
      sentAt = Date.now();
      response = await this.send(path, options, auth);
    }
    const receivedAt = Date.now();
    const data = await this.read(response, options.schema);
    return { data: data as Result<S>, sentAt, receivedAt };
  }

  async request<S extends z.ZodType | undefined = undefined>(path: string, options: RequestOptions<S> = {}): Promise<Result<S>> {
    return (await this.timed(path, options)).data;
  }

  private async send(path: string, options: RequestOptions<z.ZodType | undefined>, auth: boolean): Promise<Response> {
    const headers: Record<string, string> = {
      accept: "application/json",
      [CLIENT_PLATFORM_HEADER]: this.transport.platform,
      [CLIENT_VERSION_HEADER]: this.version,
      ...(auth ? this.transport.headers() : {}),
    };
    if (options.body !== undefined) headers["content-type"] = "application/json";
    if (options.botCheck) headers[BOT_CHECK_HEADER] = options.botCheck;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    try {
      return await this.fetchImpl(`${this.baseUrl}${path}`, {
        method: options.method ?? "GET",
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        credentials: "include",
        keepalive: options.keepalive ?? false,
        signal: controller.signal,
      });
    } catch {
      throw new NetworkError();
    } finally {
      clearTimeout(timer);
    }
  }

  private async read(response: Response, schema: z.ZodType | undefined): Promise<unknown> {
    const text = await response.text().catch(() => "");
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : undefined;
    } catch {
      body = undefined;
    }
    if (!response.ok) throw ApiError.from(response.status, body);
    if (!schema) return undefined;
    const parsed = schema.safeParse(body);
    if (!parsed.success) throw new ApiError(response.status, "INVALID_RESPONSE", "Réponse inattendue du serveur");
    return parsed.data;
  }

  /** Nouveau jeton d'accès ; une seule tentative à la fois, partagée par les requêtes qui attendent. */
  refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      try {
        const session = await this.request("/auth/refresh", {
          method: "POST",
          body: this.transport.refreshBody(),
          schema: AuthSessionResponseSchema,
          auth: false,
        });
        if (session.tokens) this.transport.save(session.tokens);
        return true;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          this.transport.save(null);
          return false;
        }
        throw error;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  // --- Points d'entrée de l'API -------------------------------------------------------------------

  config() {
    return this.timed("/config", { schema: AppConfigResponseSchema, auth: false });
  }

  me() {
    return this.request("/players/me", { schema: MeResponseSchema });
  }

  async logout(): Promise<void> {
    try {
      await this.request("/auth/logout", { method: "POST", body: this.transport.refreshBody(), auth: false });
    } finally {
      this.transport.save(null);
    }
  }

  openSession(simVersion: number) {
    return this.timed("/stations/me/session", { method: "POST", body: { simVersion }, schema: SessionResponseSchema });
  }

  sync(body: SyncRequest, keepalive = false) {
    return this.timed("/stations/me/sync", { method: "POST", body, schema: SyncResponseSchema, keepalive, timeoutMs: 20_000 });
  }

  requestCode(email: string, botCheck: string | undefined) {
    return this.request("/auth/email/request", { method: "POST", body: { email }, schema: EmailCodeSentSchema, botCheck });
  }

  async verifyCode(email: string, code: string) {
    const result = await this.request("/auth/email/verify", { method: "POST", body: { email, code }, schema: EmailVerifyResponseSchema });
    if (result.outcome !== "choice_required" && result.tokens) this.transport.save(result.tokens);
    return result;
  }

  async chooseStation(body: StationChoiceRequest) {
    const result = await this.request("/auth/email/choose", { method: "POST", body, schema: EmailVerifyResponseSchema });
    if (result.outcome !== "choice_required" && result.tokens) this.transport.save(result.tokens);
    return result;
  }

  async deleteMe(): Promise<void> {
    await this.request("/players/me", { method: "DELETE", body: { confirm: true } });
    this.transport.save(null);
  }
}
